import { inject, injectable } from 'inversify';
import { Subscription } from '@dealroadshow/socket-frontend-sdk';
import { type ISocketClient } from '@creditflowresearch/socket-client/socket-ask-ai-client';
import AskAiDITypes from '@creditflowresearch/socket-client/socket-ask-ai-client/DITypes';
import Request from '@framework/api/Rpc/Request';
import Logger from '@framework/sentry/Logger';
import { SessionService } from '@users/session/SessionService';
import {
  IAskAiAnswerPayload,
  IAskAiCancelledPayload,
  IAskAiDonePayload,
  IAskAiErrorPayload,
  IAskAiProcessingPayload,
} from '@/creditflowresearch/domain/vo/AskAiChat';

export interface IAskAiChatHandlers {
  onProcessing: (payload: IAskAiProcessingPayload) => void,
  onAnswer: (payload: IAskAiAnswerPayload) => void,
  onDone: (payload: IAskAiDonePayload) => void,
  onError: (payload: IAskAiErrorPayload) => void,
  onCancelled: (payload: IAskAiCancelledPayload) => void,
  onConnectionError: (message: string) => void,
}

export interface IAskAiAskParams {
  message: string,
  chatId?: string,
  limit?: number,
  promptVersionId: string | null,
}

enum AskAiChatEventType {
  Processing = 'ask_ai.processing',
  Answer = 'ask_ai.answer',
  Done = 'ask_ai.done',
  Error = 'ask_ai.error',
  Cancelled = 'ask_ai.cancelled',
}

const RELAY_METHOD = 'finsight_web.socket.notifications';
const ASK_AI_ENTITY = 'cfr.ask_ai';
const ASK_AI_REQUESTS_ENTITY = 'cfr.ask_ai.requests';
const NO_SESSION_MESSAGE = 'Please log in to use Ask AI.';

const CONNECTION_TIMEOUT_MS = 10000;
const UNAVAILABLE_MESSAGE = 'Ask AI is unavailable right now. Please try again.';
const CONNECTION_FAILED_MESSAGE = 'Failed to establish connection';

const STALE_AFTER_HIDDEN_MS = 30 * 1000;

@injectable()
class AskAiChatSubscriptionRepository {
  private subscription: Subscription | null = null;

  private visibilityHandler: (() => void) | null = null;

  private handlers: IAskAiChatHandlers | null = null;

  private attemptId = 0;

  private connecting: Promise<void> | null = null;

  constructor(
    @inject(AskAiDITypes.SocketClient) private readonly socket: ISocketClient,
    @inject(SessionService) private readonly sessionService: SessionService,
  ) {}

  subscribe = (handlers: IAskAiChatHandlers): Promise<void> => {
    const connecting = this.connect(handlers).finally(() => {
      if (this.connecting === connecting) this.connecting = null;
    });

    this.connecting = connecting;

    return connecting;
  };

  private connect = async (handlers: IAskAiChatHandlers): Promise<void> => {
    await this.unsubscribe();

    this.handlers = handlers;

    const userId = this.getUserId();
    if (!userId) {
      handlers.onConnectionError(NO_SESSION_MESSAGE);
      return;
    }

    const attemptId = ++this.attemptId;
    const isStale = (): boolean => attemptId !== this.attemptId;
    let settled = false;

    const connecting = this.socket.subscribe<Subscription>(this.buildSubscribeRequest(userId));

    connecting.then(
      (subscription) => {
        settled = true;

        if (isStale()) {
          subscription.cancel().catch(() => {});
          return;
        }

        this.subscription = subscription;
        this.setupEventHandlers(handlers);
        this.setupVisibilityHandler();
      },
      () => {
        settled = true;
        if (!isStale()) handlers.onConnectionError(CONNECTION_FAILED_MESSAGE);
      },
    );

    await Promise.race([
      connecting.then(() => {}, () => {}),
      new Promise<void>((resolve) => { setTimeout(resolve, CONNECTION_TIMEOUT_MS); }),
    ]);

    if (!settled && !isStale()) {
      handlers.onConnectionError(UNAVAILABLE_MESSAGE);
    }
  };

  ask = async ({ message, chatId, limit, promptVersionId }: IAskAiAskParams): Promise<void> => {
    await this.ensureSubscribed();

    if (!this.subscription) return;

    this.publish({
      action: 'ask',
      message,
      ...(chatId ? { chatId } : {}),
      ...(limit ? { limit } : {}),
      promptVersionId,
    });
  };

  cancel = (chatId: string): void => {
    this.publish({ action: 'cancel', chatId });
  };

  unsubscribe = async (): Promise<void> => {
    this.attemptId += 1;

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.subscription instanceof Subscription) {
      try {
        await this.subscription.cancel();
      } catch (error) {
        Logger.warn({ message: 'Ask AI WebSocket unsubscribe failed', error });
      }
    }

    this.subscription = null;
    this.handlers = null;
  };

  private async ensureSubscribed(): Promise<void> {
    if (this.subscription || !this.handlers) return;

    await (this.connecting ?? this.subscribe(this.handlers));
  }

  private getUserId(): number | null {
    return this.sessionService.currentSession?.userId ?? null;
  }

  private buildSubscribeRequest(userId: number): Request {
    return new Request(RELAY_METHOD, { entityId: ASK_AI_ENTITY, userId });
  }

  private publish(payload: Record<string, unknown>): void {
    const userId = this.getUserId();
    if (!userId) {
      this.handlers?.onConnectionError(NO_SESSION_MESSAGE);
      return;
    }

    const request = new Request(RELAY_METHOD, {
      entityId: ASK_AI_REQUESTS_ENTITY,
      userId,
      ...payload,
    }, {
      'socket.action': 'publish',
    });

    this.socket.rpc.call(request).catch((error: unknown) => {
      Logger.error({ message: 'Ask AI WebSocket publish failed', error, context: { action: payload.action } });
      this.handlers?.onConnectionError(UNAVAILABLE_MESSAGE);
    });
  }

  private setupEventHandlers(handlers: IAskAiChatHandlers): void {
    this.subscription!
      .on(
        AskAiChatEventType.Processing,
        ({ params: { payload } }: { params: { payload: IAskAiProcessingPayload } }) => {
          handlers.onProcessing(payload);
        },
      )
      .on(
        AskAiChatEventType.Answer,
        ({ params: { payload } }: { params: { payload: IAskAiAnswerPayload } }) => {
          handlers.onAnswer(payload);
        },
      )
      .on(
        AskAiChatEventType.Done,
        ({ params: { payload } }: { params: { payload: IAskAiDonePayload } }) => {
          handlers.onDone(payload);
        },
      )
      .on(
        AskAiChatEventType.Error,
        ({ params: { payload } }: { params: { payload: IAskAiErrorPayload } }) => {
          handlers.onError(payload);
        },
      )
      .on(
        AskAiChatEventType.Cancelled,
        ({ params: { payload } }: { params: { payload: IAskAiCancelledPayload } }) => {
          handlers.onCancelled(payload);
        },
      );
  }

  private setupVisibilityHandler(): void {
    let hiddenAt: number | null = null;

    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }

      const isStale = hiddenAt !== null && Date.now() - hiddenAt >= STALE_AFTER_HIDDEN_MS;
      hiddenAt = null;

      if (isStale) {
        const connection = this.socket.getConnection();
        connection.close();
        connection.connect();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }
}

export default AskAiChatSubscriptionRepository;
